import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getConditionTrackingByAccoundId } from '../../api/api';
import { TiTick, TiTimes } from "react-icons/ti";
import Loading from "../Loading";
import { useNullDate } from '../../hooks/useNullDate';
import { useTranslation } from 'react-i18next';
import { IoIosWarning } from "react-icons/io";

function Conditions({ accountID }) {
  const [accountConditions, setAccountConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [hoveredConditionIndex, setHoveredConditionIndex] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { i18n } = useTranslation();

  const [isLanguageReady, setIsLanguageReady] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const initializeLanguage = async () => {
      // Wait for i18n to be initialized
      await i18n.isInitialized;
      setIsLanguageReady(true);
    };

    initializeLanguage();
  }, [i18n]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isLanguageReady) return;

      try {
        setLoading(true);
        const apiData = await getConditionTrackingByAccoundId(accountID);
        if (apiData && apiData.accountConditionsList) {
          setAccountConditions(apiData.accountConditionsList);
        } else {
          console.error('Failed to fetch data for Conditions.');
        }
      } catch (error) {
        console.error('Error fetching data for Conditions:', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountID, isLanguageReady]);

  const handleModal = (condition) => {
    setSelectedCondition(condition);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  // Modify the getter functions to include fallbacks
  const getConditionName = (condition) => {
    const language = i18n.language;
    return language === 'en' 
      ? condition.conditionNameEn || condition.conditionNameFr // Fallback to French if English is missing
      : condition.conditionNameFr || condition.conditionNameEn; // Fallback to English if French is missing
  };

  const getConditionClause = (condition) => {
    const language = i18n.language;
    return language === 'en'
      ? condition.conditionTextEn || condition.conditionTextFr
      : condition.conditionTextFr || condition.conditionTextEn;
  };

  const formatDate = useNullDate();

  return (
    <>
      {accountConditions.length > 0 ? (
        <>
          {!loading ? (
            <>
              {accountConditions.map((condition, index) => (
                <div
                  onClick={() => handleModal(condition)}
                  onMouseEnter={() => setHoveredConditionIndex(index)}
                  onMouseLeave={() => setHoveredConditionIndex(null)}
                  key={index}
                  className={`border-2 p-2 flex flex-col ${hoveredConditionIndex === index ? 'h-auto transform scale-[101%]' : 'h-16 transform scale-100'} justify-between items-center w-full md:w-2/3 rounded-lg gap-2 cursor-pointer hover:bg-zinc-100 transition-all ease-in-out duration-300 hover:transition-all hover:ease-linear hover:duration-600`}
                >
                  <div className='flex items-center justify-between w-full pt-2'>
                    <span>{getConditionName(condition)}</span>
                    <div className="flex items-center gap-2">
                      {condition.isVerified ? 
                        <span className='flex gap-2 rounded-full text-green-500 items-center justify-between px-3 py-1 bg-green-50'>{t('conditionsTab.satisfied')}<TiTick className='text-green-500' size={20} /></span> 
                        : 
                        <span className='flex gap-2 rounded-full text-red-500 items-center justify-between px-3 py-1 bg-red-50'>{t('conditionsTab.unsatisfied')}<TiTimes className='text-red-500' size={20} /></span> 
                      }
                      {condition.note && <IoIosWarning className="text-yellow-500" size={20} />}
                    </div>
                  </div>
                  {hoveredConditionIndex === index && (
                    <>
                      <hr className='bg-black/5 h-0.5 w-full' />
                      <div className="w-full flex flex-col gap-2 justify-between items-start mt-2 transform transition-transform duration-300 ease-out translate-y-0">
                        <span className='max-w-md text-justify italic'>{getConditionClause(condition)}</span>
                        <div className="flex flex-row gap-2">
                          <span className='flex gap-2 rounded-full text-black/80 items-center justify-between px-3 py-1 bg-black/5'>{t('conditionsTab.recieved')} {condition.isReceived ? <TiTick className='text-green-500' size={20} /> : <TiTimes className='text-red-500' size={20} />}</span>
                          <span className='flex gap-2 rounded-full text-black/80 items-center justify-between px-3 py-1 bg-black/5'>{t('conditionsTab.verified')} {condition.isVerified ? <TiTick className='text-green-500' size={20} /> : <TiTimes className='text-red-500' size={20} />}</span>
                        </div>
                      </div>
                      <div className="w-full flex justify-normal gap-2 items-start mt-2">
                        <span className='text-sm text-gray-500'>{t('conditionsTab.recieved')}: {formatDate(condition.receiveDate)}</span>
                        <span className='w-0.5 h-full border' />
                        <span className='text-sm text-gray-500'>{t('conditionsTab.verified')}: {formatDate(condition.verifyDate)}</span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </>
          ) : (
            <Loading />
          )}
        </>
      ) : (
        <div className="w-full text-center p-5 italic text-zinc-500">
          {/* <BiError size={50} className="text-red-500 text-center w-full mb-2 animate-bounce" />No data found.<br/>Please try again later. */}
        </div>
      )}
      {isModalOpen && selectedCondition && (
        <div onClick={closeModal} className="absolute cursor-zoom-out h-screen top-0 left-0 w-full z-10 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-12 rounded-lg flex flex-col gap-4 justify-center z-50 items-center max-w-2xl relative">
            <span className="cursor-pointer text-gray-500 text-xl absolute top-2 right-2" onClick={closeModal}>&times;</span>
            <p className='text-xl font-semibold'>{getConditionName(selectedCondition)}</p>
            <p className='text-justify'>{getConditionClause(selectedCondition)}</p>
            {selectedCondition.note && 
              <p className='flex items-center flex-col justify-center w-full gap-2'>
                <span className='text-xl font-semibold'>NOTE</span>
                <span className='text-black text-justify'>{selectedCondition.note}</span>
              </p>
            }
            <div className='flex gap-2 mt-4 min-w-32'>
              <span className='flex items-center justify-between'>
                {t('conditionsTab.recieved')}:{" "}
                {selectedCondition.isReceived ? 
                  <TiTick className='text-green-500' size={20} /> : 
                  <TiTimes className='text-red-500' size={20} />
                }
              </span>
              <span className='flex items-center justify-between'>
                {t('conditionsTab.verified')}:{" "}
                {selectedCondition.isVerified ?
                  <TiTick className='text-green-500' size={20} /> : 
                  <TiTimes className='text-red-500' size={20} />
                }
              </span>
            </div>
            <div className="w-fit flex justify-normal gap-2 items-start mt-2">
              <span className='text-sm text-gray-500'>{t('conditionsTab.recieved')}: {formatDate(selectedCondition.receiveDate)}</span>
              <span className='w-0.5 h-full border' />
              <span className='text-sm text-gray-500'>{t('conditionsTab.verified')}: {formatDate(selectedCondition.verifyDate)}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

Conditions.propTypes = {
  accountID: PropTypes.string.isRequired,
};

export default Conditions;